// Session state for the online mode (plan §2.3). In offline mode (no
// VITE_API_BASE_URL) the store stays 'offline' and never talks to the
// network, so the existing local-only experience is untouched.

import { create } from 'zustand';

import { createAuthClient, type AuthClient, type SessionInfo } from './auth-client.js';
import { apiBaseUrl } from './config.js';

export type AuthStatus = 'offline' | 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  readonly status: AuthStatus;
  readonly session: SessionInfo | null;
  readonly error: string | null;
  /** Resolve the session once at startup (online mode only). */
  bootstrap(client?: AuthClient | null): Promise<void>;
  /** Adopt a session the callback page just established. */
  adoptSession(session: SessionInfo): void;
  signOut(): Promise<void>;
}

function defaultClient(): AuthClient | null {
  const base = apiBaseUrl();
  return base === null ? null : createAuthClient(base);
}

let activeClient: AuthClient | null = null;

export const useAuthStore = create<AuthState>((set) => ({
  error: null,
  session: null,
  status: 'offline',

  async bootstrap(client = defaultClient()) {
    activeClient = client;
    if (client === null) {
      set({ error: null, session: null, status: 'offline' });
      return;
    }
    set({ error: null, status: 'loading' });
    try {
      const session = await client.session();
      set(session === null ? { session: null, status: 'anonymous' } : { session, status: 'authenticated' });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : String(error),
        session: null,
        status: 'anonymous',
      });
    }
  },

  adoptSession(session) {
    set({ error: null, session, status: 'authenticated' });
  },

  async signOut() {
    if (activeClient === null) return;
    try {
      await activeClient.logout();
      set({ error: null, session: null, status: 'anonymous' });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : String(error) });
    }
  },
}));
